import clsx from 'clsx';
import type { JSX } from 'react';
import type { AvailabilitiesAddressEntry } from './availabilitiesMapListTypes';
import type { AvailabilitiesMapPanelContent } from '@/lib/types/siteSettings';
import { applyTemplate } from '@/lib/utils/textTemplates';

export function AvailabilitiesMapPanelAddressRow({
  entry,
  selected,
  content,
  onSelect,
}: {
  entry: AvailabilitiesAddressEntry;
  selected: boolean;
  content: AvailabilitiesMapPanelContent;
  onSelect: () => void;
}): JSX.Element {
  const unitCount = entry.group.units.length;
  const countLabel =
    unitCount === 1
      ? content.suiteCountSingular
      : applyTemplate(content.suiteCountPlural, { count: unitCount.toString() });
  const hasCoordinates = entry.coordinates !== null;

  return (
    <button
      type="button"
      onClick={onSelect}
      className={clsx(
        'flex w-full flex-col gap-1 rounded-2xl border px-4 py-3 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent',
        selected
          ? 'border-brand-primary bg-brand-primary/5'
          : 'border-surface-subtle bg-white hover:border-brand-primary/30 hover:bg-brand-primary/5',
      )}
    >
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-semibold text-brand-primary">{entry.addressLabel}</p>
        <span
          className={clsx(
            'inline-flex min-h-7 items-center rounded-full px-3 text-[0.7rem] font-semibold uppercase tracking-wide',
            entry.emphasis
              ? 'bg-brand-highlight/40 text-brand-primary'
              : 'bg-surface-subtle text-text-primary/70',
          )}
        >
          {countLabel}
        </span>
      </div>
      <p className="text-xs text-text-primary/70">
        {entry.group.property.submarket}
        {!hasCoordinates ? ` • ${content.mapPinFallback}` : ''}
      </p>
    </button>
  );
}
